import React, { useState, useMemo, useCallback } from 'react'
import { Modal, Form, Row, Col, InputNumber, Input, Button, message, Select, Spin, Checkbox } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { debounce } from '../../utils/debounce'

import { useRxChangeRequest } from '../../hooks/useRxChangeRequest'
import { useCaseViewContext } from '../../context'
import { useNavigation } from '../../context/NavigationContext'
import { CaseStatusEnum } from '../../constants'

const { TextArea } = Input

const RxChangeRequestModal = ({ 
  visible, 
  onCancel, 
  caseId, 
  caseDetails, 
  onSuccess 
}) => {
  const { apiClasses, apiType } = useCaseViewContext()
  const { navigateToCase } = useNavigation()
  const [form] = Form.useForm()
  const [pharmacy, setPharmacy] = useState()
  const [pharmacies, setPharmacies] = useState([])
  const [isSearching, setIsSearching] = useState(false)

  const { loading, requestRxChange } = useRxChangeRequest({ apiClasses })

  const layout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 24,
    },
  }

  const validateMessages = {
    required: "${label} is required!",
    types: {
      number: "${label} is not a valid number!",
    },
  }

  const fetchPharmacies = useCallback((name) => {
    if (name) {
      setPharmacies([])
      setIsSearching(true)

      if (apiClasses?.PharmacyAPI?.getPharmacies) {
        apiClasses.PharmacyAPI.getPharmacies(name)
          .then(({ data: { error, pharmacies } }) => {
            if (error || !pharmacies) {
              message.error("Unable to fetch Pharmacy List. Please try again!")
              return
            }

            setPharmacies(pharmacies.map((pharmacy) => ({
              ...pharmacy,
              name: pharmacy.name,
              key: pharmacy.pharmacyId,
              value: pharmacy.pharmacyId,
              state: pharmacy.state,
            })))

            message.success("Successfully fetched Pharmacy List")
          })
          .catch(({ response }) => {
            const errorMessage = "Error occurred while fetching Pharmacy List. Please try again!"
            message.error(response?.data?.message || errorMessage)
          })
          .finally(() => setIsSearching(false))
      } else {
        setIsSearching(false)
        message.error("Pharmacy search not available")
      }
    }
  }, [apiClasses])

  const debounceFetchPharmacies = useMemo(() => {
    return debounce(fetchPharmacies, 500)
  }, [fetchPharmacies])

  const handleSubmit = async (values) => {
    const data = {
      ...values,
      caseId: caseId,
    }

    if (pharmacy) data.pharmacyId = pharmacy

    if (
      caseDetails &&
      caseDetails.status &&
      [
        CaseStatusEnum.CONSULT_COMPLETE_NO_RX, 
        CaseStatusEnum.CONSULT_COMPLETE,
        CaseStatusEnum.ADDITIONAL_INFO_REQUIRED,
        CaseStatusEnum.INVALID_FACE_PHOTO,
        CaseStatusEnum.INVALID_PHOTO_ID
      ].includes(caseDetails.status)
      
    ) {
      try {
        const result = await requestRxChange(data)
        form.resetFields()
        setPharmacy(null)
        message.success("Successfully Requested Rx Change")
        onSuccess?.()
        onCancel()
        
        if (result?.newRequestId) {
          navigateToCase(caseId, result.newRequestId, apiType)
        }
      } catch (error) {
        message.error("Unable to Request Rx Change")
      }
    } else {
      message.error(
        "Can only Request For Rx-Change when case is completed or needs more information"
      )
    }
  }

  const pharmacyOptions = pharmacies.map((pharmacy) => ({ 
    key: pharmacy.key, 
    value: pharmacy.key, 
    label: `${pharmacy.name} - ${pharmacy.city}, ${pharmacy.state}` 
  }))

  return (
    <Modal
      title="Request for Rx-Change"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        {...layout}
        form={form}
        layout="vertical"
        name="rx-change-request"
        onFinish={handleSubmit}
        validateMessages={validateMessages}
      >
        <Row gutter={24} style={{ marginBottom: 20 }}>
          <Col span={24}>
            <Spin
              spinning={isSearching}
              size="small"
              indicator={<LoadingOutlined />}
            >
              <div className="ant-form-item-label">
                <label htmlFor="pharmacy">Pharmacy (Optional)</label>
              </div>
              <Select
                id="rx-change-pharmacy-select"
                size="large"
                showSearch
                allowClear
                placeholder="Find a Pharmacy"
                defaultActiveFirstOption={false}
                showArrow={false}
                filterOption={false}
                onSearch={debounceFetchPharmacies}
                onChange={setPharmacy}
                value={pharmacy}
                notFoundContent={null}
                style={{ width: '100%' }}
                options={pharmacyOptions}
              />
            </Spin>
          </Col>
        </Row>
        
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="drugName"
              label="Drug Name"
              rules={[{ required: true }]}
            >
              <Input id="rx-change-drug-name-input" size="large" placeholder="Enter Drug Name" />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Form.Item
              name="dosage"
              label="Dosage"
              rules={[{ required: true }]}
            >
              <Input id="rx-change-dosage-input" size="large" placeholder="Enter Dosage" />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Form.Item
              name="refills"
              label="Refills"
              rules={[{ required: true, type: "number" }]}
            >
              <InputNumber 
                id="rx-change-refills-input"
                size="large" 
                placeholder="Enter Refills" 
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Form.Item
              name="dispense"
              label="Dispense"
              rules={[{ required: true }]}
            >
              <Input id="rx-change-dispense-input" size="large" placeholder="Enter Dispense" />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Form.Item
              name="daysSupply"
              label="Days Supply"
              rules={[{ required: true, type: "number" }]}
            >
              <InputNumber
                id="rx-change-days-supply-input"
                size="large"
                placeholder="Enter Days Supply"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Form.Item
              name="ndc"
              label="NDC"
              rules={[{ required: true }]}
            >
              <Input id="rx-change-ndc-input" size="large" placeholder="Enter NDC" />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Form.Item
              name="pillCount"
              label="Quantity"
              rules={[{ required: true, type: "number" }]}
            >
              <InputNumber
                id="rx-change-quantity-input"
                size="large"
                placeholder="Enter Quantity"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="DAW"
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox id="rx-change-daw-checkbox">DAW (Dispense As Written)</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Reason Notes"
              name="rxChangeReason"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              rules={[{ required: true }]}
            >
              <TextArea id="rx-change-reason-textarea" autoSize={{ minRows: 3, maxRows: 5 }} />
            </Form.Item>
            
            <div style={{ textAlign: 'right', marginTop: '16px' }}>
              <Button 
                id="rx-change-submit-btn"
                type="primary" 
                size="large" 
                htmlType="submit"
                loading={loading}
              >
                Submit Rx-Change Request
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

export { RxChangeRequestModal }
